import type React from 'react'
import { useCallback, useId, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { createHistoryTxnId } from '../helpers/history-txn-id'

/** Freeze Object / Lock Object / Ignore wrap: switch ids, checked state and toggle handlers. */
export function useProtectionSwitches(params: {
  isInGroupIsolationMode: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  setActiveObjects: (objs: ShapeData[]) => void
}) {
  const { isInGroupIsolationMode, shapeManagerRef, setActiveObjects } = params
  // Each protection switch is named by its visible label (WMPCM-10578 #1): `htmlFor` gives the
  // switch its accessible name AND makes the label text itself toggle it.
  const freezeSwitchId = useId()
  const lockSwitchId = useId()
  const ignoreWrapSwitchId = useId()
  const [freezeObject, setFreezeObject] = useState(false)
  const [lockObject, setLockObject] = useState(false)
  const [ignoreWrap, setIgnoreWrap] = useState(false)
  /** True when 2+ selected objects disagree on ignoreTextWrap — UI-only, never persisted. */
  const [ignoreWrapMixed, setIgnoreWrapMixed] = useState(false)

  const syncActiveSelectionFromManager = useCallback(() => {
    const mgr = shapeManagerRef?.current

    if (!mgr) return
    const selectedIds = mgr.getSelectedShapeIds()
    const selectedSet = new Set(selectedIds)
    const selected = mgr.getShapes().filter((shape) => selectedSet.has(shape.id))

    // activeObject is derived from activeObjects[0] (see active-object-context.tsx) --
    // do not also call setActiveObject here, or a multi-selection collapses to 1 item.
    setActiveObjects(selected)
  }, [setActiveObjects, shapeManagerRef])

  /** Shared toggle flow: blocked in group isolation; UI updates only when some shape changed. */
  const runProtectionToggle = useCallback(
    (
      txnPrefix: string,
      apply: (mgr: ShapeManagerRef, historyTxnId: string) => string[],
      onChanged: () => void
    ) => {
      if (isInGroupIsolationMode) return
      const mgr = shapeManagerRef?.current

      if (!mgr) return
      const historyTxnId = createHistoryTxnId(txnPrefix)
      const changedShapeIds = apply(mgr, historyTxnId)

      if (changedShapeIds.length === 0) return
      onChanged()
      syncActiveSelectionFromManager()
    },
    [isInGroupIsolationMode, shapeManagerRef, syncActiveSelectionFromManager]
  )

  const handleFreezeObjectChange = useCallback(
    (checked: boolean) =>
      runProtectionToggle(
        'panel-freeze',
        (mgr, historyTxnId) =>
          checked ? mgr.freezeSelected({ historyTxnId }) : mgr.unfreezeSelected({ historyTxnId }),
        () => setFreezeObject(checked)
      ),
    [runProtectionToggle]
  )

  const handleLockObjectChange = useCallback(
    (checked: boolean) =>
      runProtectionToggle(
        'panel-lock',
        (mgr, historyTxnId) =>
          checked ? mgr.lockSelected({ historyTxnId }) : mgr.unlockSelected({ historyTxnId }),
        () => setLockObject(checked)
      ),
    [runProtectionToggle]
  )

  const handleIgnoreWrapChange = useCallback(
    (checked: boolean) =>
      runProtectionToggle(
        'panel-ignore-text-wrap',
        (mgr, historyTxnId) => mgr.setIgnoreTextWrapSelected(checked, { historyTxnId }),
        () => {
          setIgnoreWrap(checked)
          setIgnoreWrapMixed(false)
        }
      ),
    [runProtectionToggle]
  )

  return {
    freezeSwitchId,
    lockSwitchId,
    ignoreWrapSwitchId,
    freezeObject,
    setFreezeObject,
    lockObject,
    setLockObject,
    ignoreWrap,
    setIgnoreWrap,
    ignoreWrapMixed,
    setIgnoreWrapMixed,
    handleFreezeObjectChange,
    handleLockObjectChange,
    handleIgnoreWrapChange,
  }
}
